import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/format';
import { LogoMark, DashboardMockup } from '@/components/marketing/Mockups';

const VALUE_BULLETS = [
  ['Penawaran rapi', 'RAB akurat & konsisten, siap kirim PDF/WhatsApp.'],
  ['Proyek terpantau', 'Kanban dari ukur sampai pasang — status selalu jelas.'],
  ['Keuangan terkendali', 'DP, termin, piutang & profit per proyek dalam Rupiah.'],
];

function homePathFor(role) {
  if (role === 'super_admin') return '/admin';
  if (role === 'field') return '/tugas';
  return '/beranda';
}

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('daftar') !== null ? 'register' : 'login');
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [regForm, setRegForm] = useState({ nama: '', email: '', password: '', shop_name: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(loginForm.email, loginForm.password);
      toast.success('Berhasil masuk. Selamat datang!');
      navigate(homePathFor(u.role));
    } catch (err) {
      toast.error(getErrorMessage(err, 'Gagal masuk'));
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(regForm.nama, regForm.email, regForm.password, regForm.shop_name);
      toast.success('Akun & workshop berhasil dibuat!');
      navigate(homePathFor(u.role));
    } catch (err) {
      toast.error(getErrorMessage(err, 'Gagal mendaftar'));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-2" data-testid="login-page">
      {/* ---------------- LEFT BRAND PANEL ---------------- */}
      <aside data-testid="auth-left-panel" className="relative hidden overflow-hidden border-r border-border lg:flex lg:flex-col lg:justify-between lg:p-10">
        <div className="hero-gradient pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="auth-logo-link">
            <LogoMark className="h-10 w-10" />
            <span className="font-display text-xl font-semibold tracking-tight">AluminaOne</span>
          </Link>
          <h2 className="mt-12 max-w-md font-display text-3xl font-semibold leading-tight tracking-tight">
            Operasional workshop Anda, dalam satu platform.
          </h2>
          <ul className="mt-8 space-y-5">
            {VALUE_BULLETS.map(([title, desc]) => (
              <li key={title} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary"><Check className="h-3.5 w-3.5" /></span>
                <span>
                  <span className="block text-sm font-semibold">{title}</span>
                  <span className="text-sm text-muted-foreground">{desc}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative mt-10 max-w-md">
          <DashboardMockup />
        </div>
      </aside>

      {/* ---------------- RIGHT AUTH PANEL ---------------- */}
      <main className="flex min-h-screen items-center justify-center p-4 sm:p-6 lg:min-h-0">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors duration-150 hover:text-foreground" data-testid="login-back-home">
            <ArrowLeft className="h-4 w-4" /> Kembali ke beranda
          </Link>

          {/* Mobile brand */}
          <div className="mb-6 flex items-center gap-2 lg:hidden">
            <LogoMark className="h-10 w-10" />
            <span className="font-display text-xl font-semibold tracking-tight">AluminaOne</span>
          </div>

          <div className="mb-5">
            <h1 className="font-display text-2xl font-bold tracking-tight">
              {tab === 'login' ? 'Masuk ke akun Anda' : 'Buat akun & workshop'}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {tab === 'login' ? 'Lanjutkan mengelola penawaran, proyek & stok.' : 'Mulai gratis 14 hari. Tanpa kartu kredit.'}
            </p>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-sm)]">
            <Tabs value={tab} onValueChange={setTab} data-testid="auth-tabs">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login" data-testid="auth-switch-login">Masuk</TabsTrigger>
                <TabsTrigger value="register" data-testid="auth-switch-register">Daftar</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4 pt-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" data-testid="login-email-input" type="email" required value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="nama@email.com" className="min-h-[44px]" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password">Kata Sandi</Label>
                    <Input id="password" data-testid="login-password-input" type="password" required value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="••••••••" className="min-h-[44px]" />
                  </div>
                  <Button type="submit" data-testid="login-submit-button" disabled={loading} className="w-full min-h-[44px] rounded-xl">
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Masuk
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4 pt-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="shopname">Nama Usaha / Workshop</Label>
                    <Input id="shopname" data-testid="register-shopname-input" required value={regForm.shop_name}
                      onChange={(e) => setRegForm({ ...regForm, shop_name: e.target.value })} placeholder="cth: Workshop Aluminium Jaya" className="min-h-[44px]" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="nama">Nama Anda</Label>
                    <Input id="nama" data-testid="register-nama-input" required value={regForm.nama}
                      onChange={(e) => setRegForm({ ...regForm, nama: e.target.value })} placeholder="Nama Anda" className="min-h-[44px]" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="remail">Email</Label>
                    <Input id="remail" data-testid="register-email-input" type="email" required value={regForm.email}
                      onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} placeholder="nama@email.com" className="min-h-[44px]" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rpass">Kata Sandi</Label>
                    <Input id="rpass" data-testid="register-password-input" type="password" required minLength={6} value={regForm.password}
                      onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} placeholder="Min. 6 karakter" className="min-h-[44px]" />
                  </div>
                  <Button type="submit" data-testid="register-submit-button" disabled={loading} className="w-full min-h-[44px] rounded-xl">
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Daftar & Buat Workshop
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">Akun pertama menjadi <b>Owner</b>. Anda bisa menambah Staff & Tukang dari menu Pengaturan.</p>
                </form>
              </TabsContent>
            </Tabs>
          </div>

          <p className="mt-5 text-center text-xs text-muted-foreground">
            Dengan melanjutkan, Anda menyetujui Syarat Layanan & Kebijakan Privasi AluminaOne.
          </p>
        </div>
      </main>
    </div>
  );
}
